import { isProjectsInJiraM2Enabled } from '@jira/platform__services-native-integration-projects/src/index.tsx';
import { isCustomFilter } from '@jira/issue-navigator__actions-common/src/utils/filters/index.tsx';
import {
	getMaxIssuesPerPage,
	ISSUE_SEARCH_NAMESPACE,
	MAX_AMOUNT_OF_COLUMNS,
	NATURAL_LANGUAGE_TO_JQL,
	NIN_GLOBAL_OPERATION_SCOPE,
	NIN_PROJECT_OPERATION_SCOPE,
} from '@jira/issue-navigator__issue-navigator/src/common/constants.tsx';
import { convertFilterIdToIssueNavigatorId } from '@jira/issue-navigator__issue-navigator/src/common/utils/index.tsx';
import { getOperationScope } from '@jira/issue-table__issue-table-common/src/utils/index.tsx';
import { createViewAri } from '@jira/issue-navigator__view-ari/src/index.tsx';
import type {
	JiraAtlassianIntelligenceFeatureEnum,
	main_IssueNavigatorQuery$variables,
	JiraIssueSearchOperationScope,
	JiraIssueSearchScope,
} from '@jira/issue-navigator__issue-navigator/src/__generated__/main_IssueNavigatorQuery.graphql';
import type { TenantContext } from '@jira/platform__tenant-context-types/src/tenant-context.tsx';
import type { RouterContext } from '@atlassian/react-resource-router';
import { fg } from '@atlassian/jira-feature-gating';
import { expVal } from '@atlassian/jira-feature-experiments';
import { getIssueSearchInput, getParams } from './getIssueSearchInput.tsx';
import { getIssueSearchResourceKey } from './getIssueSearchResourceKey.tsx';
import { isChromelessRoute } from './isChromelessRoute.tsx';

/**
 * Builds the variables for `main_IssueNavigatorQuery` from the router context and tenant context.
 *
 * This is a pure function — it does NOT call loadQuery, earlyFetch, or anything async.
 * Both the router resource (getData) and the vendorless early-script entry use this
 * function to ensure byte-identical variables and therefore matching getEarlyScriptPromiseKey().
 */
export const buildMainIssueNavigatorQueryVariables = (
	routerContext: RouterContext,
	tenantContext: TenantContext,
): main_IssueNavigatorQuery$variables => {
	const {
		projectKey,
		issueKey,
		jql: jqlParam,
		defaultJql,
		filter,
		groupBy,
		hideDone,
	} = getParams(routerContext);

	const issueSearchInput = getIssueSearchInput(defaultJql, jqlParam, filter, projectKey);

	const maxIssuesPerPage = getMaxIssuesPerPage();
	const chromelessRoute = isChromelessRoute(routerContext);
	let viewId;
	let shouldQueryConditionalFormatting = Boolean(projectKey);
	let isIFCEnabled = Boolean(projectKey);

	const filterId = Boolean(filter) && isCustomFilter(filter) ? filter : null;
	if (chromelessRoute) {
		// Disable conditional formatting for chromeless (i.e. embed) routes when saved views is disabled
		shouldQueryConditionalFormatting = fg('saved_list_view_technical_changes');
		// Disable IFC for chromeless (i.e. embed) routes
		isIFCEnabled = false;
		// Explicitly load list view for chromeless (i.e. embed) routes
		viewId = filterId ? convertFilterIdToIssueNavigatorId(filterId) : 'list_default';
	}

	let staticViewInput;
	if (projectKey === undefined || projectKey === null) {
		staticViewInput = {
			isHierarchyEnabled: false,
			isGroupingEnabled: false,
			isHideDoneEnabled: false,
		};
	} else if (hideDone != null) {
		staticViewInput = { isHideDoneEnabled: hideDone };
	}

	const viewConfigInput = staticViewInput
		? { staticViewInput }
		: { viewInput: { namespace: ISSUE_SEARCH_NAMESPACE } };

	let jiraViewQueryInput: main_IssueNavigatorQuery$variables['jiraViewQueryInput'] = null;

	if (fg('saved_list_view_technical_changes')) {
		const itemIdSlug = routerContext.match.params.navigationItemId;
		const itemId = itemIdSlug ? `list/${itemIdSlug}` : 'list';

		if (isProjectsInJiraM2Enabled()) {
			const subspaceId = routerContext.match.params.subspaceId;
			if (subspaceId) {
				// Subcontainer (subspace) scoped view
				jiraViewQueryInput = {
					subcontainerIdAndItemQuery: {
						cloudId: tenantContext.cloudId,
						itemId,
						subcontainerId: subspaceId,
					},
				};
			} else if (projectKey == null) {
				jiraViewQueryInput = {
					viewId: createViewAri({
						activationId: tenantContext.activationId,
						cloudId: tenantContext.cloudId,
					}),
				};
			} else {
				jiraViewQueryInput = {
					projectKeyAndItemQuery: {
						cloudId: tenantContext.cloudId,
						projectKey,
						itemId,
					},
				};
			}
		} else {
			jiraViewQueryInput =
				projectKey == null
					? {
							viewId: createViewAri({
								activationId: tenantContext.activationId,
								cloudId: tenantContext.cloudId,
							}),
						}
					: {
							projectKeyAndItemQuery: {
								cloudId: tenantContext.cloudId,
								projectKey,
								itemId,
							},
						};
		}
	}

	let operationScope: JiraIssueSearchOperationScope;

	if (isIFCEnabled && expVal('jira_inline_field_config', 'isInlineFieldConfigEnabled', false)) {
		operationScope = getOperationScope(projectKey);
	} else if (projectKey) {
		operationScope = NIN_PROJECT_OPERATION_SCOPE;
	} else {
		operationScope = NIN_GLOBAL_OPERATION_SCOPE;
	}

	const scope: JiraIssueSearchScope = {
		operationScope,
		projectKey: projectKey ?? null,
	};

	const fieldSetsScope = {
		operationScope,
		projectKey: projectKey ?? null,
	};

	const dynamicSearchKey = getIssueSearchResourceKey(routerContext);

	const variables: main_IssueNavigatorQuery$variables = {
		cloudId: tenantContext.cloudId,
		first: issueKey == null ? maxIssuesPerPage : null,
		issueSearchInput,
		namespace: ISSUE_SEARCH_NAMESPACE,
		fieldSetsInput: {
			viewInput: {
				namespace: ISSUE_SEARCH_NAMESPACE,
				filterId,
				viewId,
			},
		},
		after: null,
		options: { issueKey },
		filterId,
		amountOfColumns: MAX_AMOUNT_OF_COLUMNS,
		shouldQueryHasChildrenWithCount: projectKey !== null && projectKey !== undefined,
		scope,
		viewConfigInput,
		staticViewInput,
		jiraViewQueryInput,
		settings: fg('saved_list_view_technical_changes')
			? {
					groupBy,
					hideDone,
					jql: 'jql' in issueSearchInput ? issueSearchInput.jql : undefined,
				}
			: null,
		atlassianIntelligenceProductFeature:
			// oxlint-disable-next-line typescript/consistent-type-assertions
			NATURAL_LANGUAGE_TO_JQL as JiraAtlassianIntelligenceFeatureEnum,
		projectKey: projectKey ?? '',
		shouldQueryProject: Boolean(projectKey),
		shouldQueryConditionalFormatting,
		shouldQueryConditionalFormattingFromSavedView:
			shouldQueryConditionalFormatting && fg('saved_list_view_technical_changes'),
		shouldQuerySaveResetViewSettingsButton:
			!tenantContext.isAnonymous && expVal('saved_list_view', 'isEnabled', false),
		groupBy: groupBy ?? null,
		shouldQueryRankField:
			Boolean(projectKey) && expVal('pnin_jql_order_by_rank_enabled', 'isEnabled', false),
		fieldSetsScope,
		viewId,
		/*
		This key is used to generate a unique identity for the issue connection record in the Relay store when running a
		new search. This is to prevent concurrency related bugs occurring when performing a refetch that returns a new
		connection of results. Ordinarily this would be handled automatically when relevant query variables change (as
		these are used to form the connection key), however we override the default connection filters which allows us
		to modify variables during pagination operations without producing a new connection key.
		 */
		dynamicSearchKey,
	};

	return variables;
};
